import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { ProvisionInviteeDocument, ProvisionInviteeOutcome } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

// Invite one outsider by email. genesis creates the Keycloak account and sends
// an email-bound, single-use link; the invitee sets a password there, never
// through a registration form.
//
// The three outcomes are all successful responses, not errors: only a transport
// or permission failure routes through userErrorMessage(). CONFLICT in
// particular is deliberate genesis behaviour - the submitted address belongs to
// an established account, which was left untouched. The copy stays vague about
// which ("already has access") because confirming an account exists to anyone
// holding the invite role would leak the user directory.
//
// The dialog does not report *who* was invited after the fact: genesis exposes
// no query for pending invitations, so the result state is all we can honestly
// show. Once such a query exists, this belongs on a settings surface that can
// list and re-send them, not in a fire-and-forget dialog.

// Deliberately permissive: the field only catches obvious typos before spending
// a round trip. Keycloak is the authority on what a valid address is, and a
// stricter pattern here would reject deliverable addresses.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Result = { kind: "outcome"; outcome: ProvisionInviteeOutcome } | { kind: "error"; message: string };

interface InviteOutsiderDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteOutsiderDialog({ open, onClose }: InviteOutsiderDialogProps) {
  const intl = useIntl();
  const [email, setEmail] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [invite, { loading }] = useMutation(ProvisionInviteeDocument);

  const reset = () => {
    setEmail("");
    setInvalid(false);
    setResult(null);
  };

  const close = () => {
    onClose();
    reset();
  };

  const submit = async () => {
    const trimmed = email.trim();
    if (!EMAIL_SHAPE.test(trimmed)) {
      setInvalid(true);
      setResult(null);
      return;
    }
    setInvalid(false);
    setResult(null);
    try {
      const { data } = await invite({ variables: { input: { email: trimmed } } });
      const outcome = data?.provisionInvitee.outcome;
      if (!outcome) throw new Error("provisionInvitee returned no outcome");
      setResult({ kind: "outcome", outcome });
      // Keep a rejected address on screen so the user can correct a typo; clear
      // it once the invitation is on its way, so the next one starts empty.
      if (outcome !== ProvisionInviteeOutcome.Conflict) setEmail("");
    } catch (e) {
      setResult({ kind: "error", message: userErrorMessage(intl, e) });
    }
  };

  return (
    <Modal
      id="invite-dialog"
      open={open}
      onClose={close}
      dismiss="explicit"
      dirty={email !== ""}
      preventClose={loading}
      title={intl.formatMessage({ id: "invite.eyebrow", defaultMessage: "Access · Invitation" })}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button id="invite-submit" variant="primary" onClick={() => void submit()} disabled={loading || email.trim() === ""}>
            {loading ? (
              <FormattedMessage id="invite.sending" defaultMessage="Sending…" />
            ) : (
              <FormattedMessage id="invite.submit" defaultMessage="Send invitation" />
            )}
          </Button>
        </>
      }
    >
      <h2 className="font-serif text-smycles-display-s leading-tight">
        <FormattedMessage id="invite.heading" defaultMessage="Invite an external person" />
      </h2>
      <p className="mt-2 text-smycles-meta leading-relaxed text-ink">
        <FormattedMessage
          id="invite.intro"
          defaultMessage="They receive an access link valid for 72 hours and set their own password. The link works once and only for this address."
        />
      </p>

      <label className="smycles-field-label mb-1 mt-6 block" htmlFor="invite-email">
        <FormattedMessage id="invite.emailLabel" defaultMessage="Email address" />
      </label>
      <input
        id="invite-email"
        type="email"
        value={email}
        autoComplete="off"
        placeholder={intl.formatMessage({
          id: "invite.emailPlaceholder",
          defaultMessage: "name@partner.example",
        })}
        onChange={(e) => {
          setEmail(e.target.value);
          setInvalid(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit();
        }}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? "invite-email-error" : undefined}
        className="smycles-field-value w-full rounded-md bg-surface px-3 py-2.5 ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-ink-accent-ui"
      />

      {invalid ? (
        <p id="invite-email-error" className="mt-2 text-smycles-meta text-ink-accent">
          <FormattedMessage id="invite.invalidEmail" defaultMessage="Please enter a valid email address." />
        </p>
      ) : null}

      {result ? (
        <p
          id="invite-result"
          className="mt-4 text-smycles-meta leading-relaxed text-ink"
          role={result.kind === "error" ? "alert" : "status"}
        >
          {result.kind === "error" ? result.message : <OutcomeMessage outcome={result.outcome} />}
        </p>
      ) : null}
    </Modal>
  );
}

function OutcomeMessage({ outcome }: { outcome: ProvisionInviteeOutcome }) {
  switch (outcome) {
    case ProvisionInviteeOutcome.Created:
      return <FormattedMessage id="invite.created" defaultMessage="Invitation sent." />;
    case ProvisionInviteeOutcome.Resent:
      return (
        <FormattedMessage
          id="invite.resent"
          defaultMessage="This person had already been invited. The access link was sent again."
        />
      );
    case ProvisionInviteeOutcome.Conflict:
      return (
        <FormattedMessage
          id="invite.conflict"
          defaultMessage="This address already has access, so no invitation was sent."
        />
      );
  }
}
